import { UIIcon } from '../config/icons';
import { ABFilter, ABFilterBar } from '../lib/ab/filter-bar';
import { ABObjectCount } from '../lib/ab/widgets-admin';
import { CBookingAdminView, cBookingsAdminView } from '../lib/mdl/c-booking-admin';
import { CVideoPurchaseAdminView, cVideoPurchaseRefund, cVideoPurchasesAdminView } from '../lib/mdl/c-video-purchase';
import { UITableDefinition, UITable } from '../lib/ui/component/table';
import { uiToast } from '../lib/ui/component/toast';
import { UICheckbox } from '../lib/ui/component/toggle';
import { $el } from '../lib/ui/elements';
import { $fmt } from '../lib/ui/fmt';
import { UIScreen } from '../lib/ui/screen';
import { UIViewSpec } from '../lib/ui/view';
import { YArrayDataSource } from '../lib/y/data-source';
import { $on } from '../lib/y/events';
import { $form } from '../lib/y/form';
import { $location } from '../lib/y/location';
import { YTime, $time } from '../lib/y/time';

function filterBookings(bookings: CBookingAdminView[], filters: ABFilter[]) {
    let from: YTime | undefined;
    let to: YTime | undefined;

    for(const f of filters) {
        if(f.key == 'city')
            bookings = bookings.filter(b => f.value.includes(b.city_id || ''));
        else if(f.key == 'producer')
            bookings = bookings.filter(b => f.value.includes(b.producer_id || ''));
        else if(f.key == 'from_date')
            from = f.value.start;
        else if(f.key == 'to_date')
            to = f.value.end;
    }

    if(from || to) {
        bookings = bookings.filter(b => $time(b.session_time).inRange(from, to));
    }

    return bookings;
}

type BookingFilterStatus = 'active' | 'used' | 'standby' | 'reserved' | 'cancelled';

function getBookingFilterStatus(booking: CBookingAdminView): BookingFilterStatus {
    if(booking.status == 'confirmed') {
        if($time(booking.session_time).is_future)
            return 'active';
        else
            return 'used';
    } else if(booking.status == 'cancelled') {
        return 'cancelled';
    } else if(booking.status == 'standby') {
        return 'standby';
    } else if(booking.status == 'reserved' || booking.status == 'standby_reserved') {
        return 'reserved';
    }
    return 'cancelled';
}

export class AdminBookingsScreen extends UIScreen {
    protected override async fillContent(content: UIViewSpec) {
        const doSearch = () => {
            const filter = form.value;
            let bookings = all_bookings.filter(b => filter[getBookingFilterStatus(b)]);
            bookings = filterBookings(bookings, filters.filters);
            bookings = filter_bar.search_input!.filterArray(bookings, (b, w) => {
                if(b.event_name.toLowerCase().includes(w)) return true;
                if(b.user_first_name.toLowerCase().includes(w)) return true;
                if(b.user_last_name.toLowerCase().includes(w)) return true;
                if(b.user_email.toLowerCase().includes(w)) return true;
                if(b.number.includes(w)) return true;
                return false;
            });

            bookings.sort((a, b) => a.created_at < b.created_at ? 1 : -1);

            source.data = bookings;
        }

        const active = new UICheckbox({ title: $el.SPAN('tl-active-row', 'Active') });
        const used = new UICheckbox({ title: 'Used' });
        const standby = new UICheckbox({ title: $el.SPAN('tl-standby-row', 'Standby') });
        const reserved = new UICheckbox({ title: $el.SPAN('tl-reserved-row', 'Reserved') });
        const cancelled = new UICheckbox({ title: $el.SPAN('tl-canceled-row', 'Cancelled') });

        active.value = true;
        used.value = true;

        const form = $form({ active: active, used: used, standby: standby, reserved: reserved, canceled: cancelled });

        const filter_bar = new ABFilterBar({
            on_filter: doSearch,
            on_search: doSearch,
            right_content: [active, used, standby, reserved, cancelled],
            no_header: true
        });

        const filters = filter_bar.filters;
        filters.addCity();
        filters.addFromDate();
        filters.addToDate();

        const def = new UITableDefinition<CBookingAdminView>();
        def.addValue(b => b.event_name, { title: 'Event', width: 200 });
        def.addValue(b => $time(b.session_time), $fmt.DATE('d MMM yyyy HH:mm'), { title: 'Session', width: -150 });
        def.addValue(b => b.user_first_name + ' ' + b.user_last_name, {
            title: 'User', width: 160, classes: 'u-clickable', on_click: row => {
                $location.push('user', { user_id: row.item.user_id });
            }
        });
        def.addValue(b => b.quantity, $fmt.NUMBER, { title: 'Qty', width: -70 });
        def.addValue(b => b.number, { title: 'Number', width: -90 })
        def.addValue(b => $time(b.updated_at), $fmt.DATE('d MMM yyyy HH:mm'), { title: 'Updated at', width: -150 });

        def.preference_prefix = 'bookings';

        def.controller = row => {
            const status = row.item.status;

            if(status == 'confirmed') {
                if($time(row.item.session_time).is_future)
                    row.classList.add('tl-active-row');
            } else if(status == 'cancelled') {
                row.classList.add('tl-canceled-row');
            } else if(status == 'standby') {
                row.classList.add('tl-standby-row');
            } else if(status == 'reserved' || status == 'standby_reserved') {
                row.classList.add('tl-reserved-row');
            }
            return undefined;
        }

        const source = new YArrayDataSource<CBookingAdminView>();

        let all_bookings = await cBookingsAdminView();

        content.ui_add([
            $el.SECTION('pad-t-narrow', $el.DIV('tl-new-list-block', [
                filter_bar,
                new UITable(def, source),
            ]))
        ]);

        $on(used, 'value', doSearch);
        $on(active, 'value', doSearch);
        $on(standby, 'value', doSearch);
        $on(cancelled, 'value', doSearch);
        $on(reserved, 'value', doSearch);

        doSearch();
    }
}

function filterPurchases(purchases: CVideoPurchaseAdminView[], filters: ABFilter[]) {
    for(const f of filters) {
        if(f.key == 'producer')
            purchases = purchases.filter(p => f.value.includes(p.producer_id));
        else if(f.key == 'event')
            purchases = purchases.filter(p => f.value.includes(p.event_id));
    }

    return purchases;
}
export class AdminPurchasesScreen extends UIScreen {
    protected override async fillContent(content: UIViewSpec) {
        const all_purchases = await cVideoPurchasesAdminView();
        const event_map = new Map(all_purchases.map(p => [p.event_id, p.event_name]));
        const producer_set = new Set(all_purchases.map(p => p.producer_id));

        const doSearch = () => {
            let purchases = filterPurchases(all_purchases, filters.filters);

            purchases = filter_bar.search_input!.filterArray(purchases, (b, w) => {
                if(b.event_name.toLowerCase().includes(w)) return true;
                if(b.user_name.toLowerCase().includes(w)) return true;
                if(b.user_email.toLowerCase().includes(w)) return true;
                return false;
            });

            purchases.sort((a, b) => a.created_at < b.created_at ? 1 : -1);

            source.data = purchases;

            count.number = purchases.reduce((n, p) => n + (!p.is_voided ? 1 : 0), 0);
            sek_count.number = purchases.reduce((n, p) => n + (!p.is_voided ? p.price.value / 100 : 0), 0);
        }

        const count = new ABObjectCount('purchases');
        const sek_count = new ABObjectCount('sek');

        const filter_bar = new ABFilterBar({
            on_filter: doSearch,
            on_search: doSearch,
            right_content: [count, sek_count],
            no_header: true
        });

        const filters = filter_bar.filters;
        filters.addFilterButton();
        filters.addProducer(producer_set);
        filters.addCustom('event', 'Event', () => Array.from(event_map.entries()).map(e => ({ key: e[0], name: e[1] })));

        const def = new UITableDefinition<CVideoPurchaseAdminView>();
        def.addValue(p => p.event_name, { title: 'Event', width: 200 });
        def.addValue(b => b.user_name, {
            title: 'User', width: 160, classes: 'u-clickable', on_click: row => {
                $location.push('user', { user_id: row.item.user_id });
            }
        });
        def.addValue(b => b.price, $fmt.CURRENCY, { title: 'Price', width: -90 });
        def.addValue(b => $time(b.created_at), $fmt.DATE('d MMM yyyy HH:mm'), { title: 'Purchased at', width: -150 });
        def.addButtons([
            {
                icon: UIIcon.TRASH_CAN, tooltip: 'Refund', when: item => !item.is_voided, action: (item, row) => {
                    cVideoPurchaseRefund(item.id).then(() => {
                        item.is_voided = true;
                        row.classList.add('tl-canceled-row');
                        row.reload();
                    }, uiToast);
                }
            },
        ], { width: -31 });

        def.preference_prefix = 'purchases';

        def.controller = row => {
            if(row.item.is_voided)
                row.classList.add('tl-canceled-row');

            return undefined;
        }

        const source = new YArrayDataSource<CVideoPurchaseAdminView>();

        content.ui_add([
            $el.SECTION('pad-t-narrow', $el.DIV('tl-new-list-block', [
                filter_bar,
                new UITable(def, source),
            ]))
        ]);

        doSearch();
    }
}